import { useEffect, useState } from 'react'

import { ServiceGroupAccordion } from '@/components/ServiceGroupAccordion/ServiceGroupAccordion'
import { TicketConstraintLines } from '@/components/TicketConstraintLines/TicketConstraintLines'
import { useAppSelector } from '@/hooks/store'
import { Service, ServiceGroup } from '@/models/service'
import {
  mapPageGroupReservationErrorsSelector,
  mapPageGroupSpotErrorsSelector,
  mapPageSpotErrorsSelector,
} from '@/store/cartSlice/selectors/mapPageErrorsSelector'
import { ticketSlice } from '@/store/ticketSlice'

import { SetupServiceListItem } from './SetupServiceListItem'

interface Props {
  getOverrideValues: (service: Service) => Partial<Service>
  groups: ServiceGroup[]
  isMultiGroup: boolean
  submitted: boolean
}

export function SetupTicketGroups({
  getOverrideValues,
  groups,
  isMultiGroup,
  submitted,
}: Props) {
  const spotErrors = useAppSelector(mapPageSpotErrorsSelector)
  const groupReservationErrors = useAppSelector(
    mapPageGroupReservationErrorsSelector
  )
  const groupSpotErrors = useAppSelector(mapPageGroupSpotErrorsSelector)
  const validationScope = useAppSelector(ticketSlice.selectors.validationScope)
  const showReservationErrors = submitted && validationScope === 'reservation'
  const showSpotErrors = submitted && validationScope === 'spot'
  const [expandedGroupIds, setExpandedGroupIds] = useState<number[]>([])

  useEffect(() => {
    if (groups.length === 0) {
      return
    }

    setExpandedGroupIds((current) => {
      if (groups.length === 1) {
        return [groups[0].serviceGroupId]
      }

      if (current.length === 0) {
        return [groups[0].serviceGroupId]
      }

      return current.filter((groupId) =>
        groups.some((group) => group.serviceGroupId === groupId)
      )
    })
  }, [groups])

  useEffect(() => {
    if (!submitted) {
      return
    }

    const groupsWithErrors = [
      ...(showReservationErrors
        ? groupReservationErrors.map((error) => error.groupId)
        : []),
      ...(showSpotErrors ? groupSpotErrors.map((error) => error.groupId) : []),
    ]
    if (groupsWithErrors.length === 0) {
      return
    }

    setExpandedGroupIds((current) => [
      ...new Set([...current, ...groupsWithErrors]),
    ])
  }, [
    submitted,
    showReservationErrors,
    showSpotErrors,
    groupReservationErrors,
    groupSpotErrors,
  ])

  const toggleGroup = (groupId: number) => {
    setExpandedGroupIds((current) =>
      current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : [...current, groupId]
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => {
        const groupReservationMinError = groupReservationErrors.find(
          (error) =>
            error.groupId === group.serviceGroupId &&
            error.type === 'groupMinQty'
        )
        const groupReservationMaxError = groupReservationErrors.find(
          (error) =>
            error.groupId === group.serviceGroupId &&
            error.type === 'groupMaxQty'
        )
        const groupSpotMinError = groupSpotErrors.find(
          (error) =>
            error.groupId === group.serviceGroupId &&
            error.type === 'spotMinQty'
        )
        const groupSpotMaxError = groupSpotErrors.find(
          (error) =>
            error.groupId === group.serviceGroupId &&
            error.type === 'spotMaxQty'
        )

        const hasGroupError =
          (showReservationErrors &&
            (Boolean(groupReservationMinError) ||
              Boolean(groupReservationMaxError))) ||
          (showSpotErrors &&
            (Boolean(groupSpotMinError) || Boolean(groupSpotMaxError)))

        return (
          <ServiceGroupAccordion
            hasError={hasGroupError}
            hasReservationMaxError={
              showReservationErrors && Boolean(groupReservationMaxError)
            }
            hasReservationMinError={
              showReservationErrors && Boolean(groupReservationMinError)
            }
            hasSpotMaxError={showSpotErrors && Boolean(groupSpotMaxError)}
            hasSpotMinError={showSpotErrors && Boolean(groupSpotMinError)}
            isAccordion={isMultiGroup}
            isExpanded={expandedGroupIds.includes(group.serviceGroupId)}
            key={group.serviceGroupId}
            maximumReservationQuantity={group.maximumReservationQuantity}
            maximumSpotQuantity={group.maximumSpotQuantity}
            minimumReservationQuantity={group.minimumReservationQuantity}
            minimumSpotQuantity={group.minimumSpotQuantity}
            name={group.name}
            onToggle={() => toggleGroup(group.serviceGroupId)}
            reservationErrorAnchor={`group-${group.serviceGroupId}-reservation`}
            spotErrorAnchor={`group-${group.serviceGroupId}-spot`}
          >
            {group.services.map((service) => {
              const listItemService = {
                ...service,
                ...getOverrideValues(service),
              }

              const serviceError = spotErrors.find(
                (error) =>
                  error.serviceId === service.serviceId &&
                  (error.type === 'minQty' || error.type === 'maxQty')
              )

              return (
                <div
                  data-ticket-error-anchor={
                    showSpotErrors && serviceError
                      ? `service-${service.serviceId}`
                      : undefined
                  }
                  key={service.serviceId}
                >
                  <SetupServiceListItem service={listItemService} />
                </div>
              )
            })}
          </ServiceGroupAccordion>
        )
      })}
    </div>
  )
}

export function SetupTicketGlobalConstraints({
  reservationMinimumQuantity,
  reservationLimitQuantity,
  spotMinimumQuantity,
  spotLimitQuantity,
  hasReservationMinError,
  hasReservationMaxError,
  hasSpotMinError,
  hasSpotMaxError,
}: {
  reservationMinimumQuantity: number
  reservationLimitQuantity: number
  spotMinimumQuantity: number
  spotLimitQuantity: number
  hasReservationMinError: boolean
  hasReservationMaxError: boolean
  hasSpotMinError: boolean
  hasSpotMaxError: boolean
}) {
  const hasAnyConstraint =
    reservationMinimumQuantity > 0 ||
    reservationLimitQuantity > 0 ||
    spotMinimumQuantity > 0 ||
    spotLimitQuantity > 0

  if (!hasAnyConstraint) {
    return null
  }

  return (
    <div className="mb-1">
      <TicketConstraintLines
        constraintScope="total"
        errorAnchor="global-reservation"
        hasMaxError={hasReservationMaxError}
        hasMinError={hasReservationMinError}
        maximumQuantity={reservationLimitQuantity}
        minimumQuantity={reservationMinimumQuantity}
      />
      <TicketConstraintLines
        constraintScope="spot"
        errorAnchor="global-spot"
        hasMaxError={hasSpotMaxError}
        hasMinError={hasSpotMinError}
        maximumQuantity={spotLimitQuantity}
        minimumQuantity={spotMinimumQuantity}
      />
    </div>
  )
}
